/**
 * DetailsStep Component
 * Step 4: Order summary, services, warnings, placement instructions, and pricing
 */

import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import type { OrderForm } from '../../../schemas/order.schema';
import { InfoIcon } from 'lucide-react';
import { MaterialWarnings } from '../../../components/widget/MaterialWarnings';
import { CategorizedAcknowledgments } from '../../../components/widget/CategorizedAcknowledgments';
import { OrderSummaryCard } from '../../../components/widget/OrderSummaryCard';
import { RealTimePricing } from '../../../components/pricing/RealTimePricing';
import type { LocationData, ContainerSelection, ScheduleSelection } from '../../../types/order-builder';
import { usePricingEstimation } from '../../../hooks/usePricingEstimation';
import { useProviderConfig } from '../../../contexts/ProviderConfigContext';
import { buildPricingRequest } from '../../../services/pricing-request-builder.service';

export function DetailsStep() {
  const { register, watch, setValue } = useFormContext<OrderForm>();
  const { providerSlug } = useParams<{ providerSlug: string }>();
  const { config: providerConfig } = useProviderConfig();

  // Watch acknowledged terms from form state (single source of truth)
  const watchedAcknowledgedTerms = watch('acknowledgedTerms');
  const acknowledgedTerms = useMemo(() => watchedAcknowledgedTerms || [], [watchedAcknowledgedTerms]);

  // Handler for toggling acknowledgments
  const handleAcknowledgmentToggle = (acknowledgment: string) => {
    const updated = acknowledgedTerms.includes(acknowledgment)
      ? acknowledgedTerms.filter(item => item !== acknowledgment)
      : [...acknowledgedTerms, acknowledgment];

    setValue('acknowledgedTerms', updated, { shouldValidate: true });
  };

  // Watch all required form fields
  const street = watch('street');
  const city = watch('city');
  const state = watch('state');
  const zipCode = watch('zipCode');
  const containerSize = watch('containerSize');
  const materialType = watch('materialType');
  const deliveryDate = watch('deliveryDate');
  const instructions = watch('placementInstructions');
  const estimatedTonnage = watch('estimatedTonnage');
  const hasTreatedWood = watch('hasTreatedWood');
  const estimatedWeightTons = watch('estimatedWeightTons');

  // Watch selected services
  const watchedServices = watch('selectedServices');

  const charCount = instructions?.length || 0;
  const maxChars = 500;

  // Build pricing params from form values using domain service
  const pricingParams = useMemo(() => {
    return buildPricingRequest({
      containerSize,
      materialType,
      selectedServices: watchedServices,
      estimatedTonnage,
      hasTreatedWood,
      estimatedWeightTons,
    });
  }, [containerSize, materialType, watchedServices, estimatedTonnage, hasTreatedWood, estimatedWeightTons]);

  // Get pricing data for warnings and acknowledgments (uses React Query cache)
  const { pricing } = usePricingEstimation(
    providerSlug || '',
    pricingParams
  );

  // Extract prohibited materials from provider config
  const prohibitedMaterials = useMemo(() => {
    if (!providerConfig?.prohibitedItems) return [];
    // Flatten all items from all categories into a single array
    return providerConfig.prohibitedItems.flatMap(category => category.items);
  }, [providerConfig]);

  // Check if all required acknowledgments are met
  const allAcknowledgmentsMet = useMemo(() => {
    const required = pricing?.requiredAcknowledgments || [];
    if (required.length === 0) return true;
    // New format: check by ID
    return required.every(ack => acknowledgedTerms.includes(ack.id));
  }, [pricing?.requiredAcknowledgments, acknowledgedTerms]);

  // Check if required data is present
  const hasLocation = street && city && state && zipCode;
  const hasContainer = containerSize && materialType;
  const hasSchedule = deliveryDate;

  // Don't render if required data is missing
  if (!hasLocation || !hasContainer || !hasSchedule) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Please complete previous steps to continue.</p>
      </div>
    );
  }

  // Build data objects from form values
  const location: LocationData = {
    address: street,
    city,
    state,
    zipCode,
  };

  const container: ContainerSelection = {
    size: containerSize,
    materialType,
    basePrice: 0, // Will be calculated by pricing API
    estimatedTonnage,
    hasTreatedWood,
    estimatedWeightTons,
  };

  const schedule: ScheduleSelection = {
    deliveryDate: new Date(deliveryDate),
    isASAP: false, // Could be derived from form if we add this field
  };

  return (
    <div className="space-y-8">
      {/* Order Summary */}
      <OrderSummaryCard
        location={location}
        container={container}
        schedule={schedule}
      />

      {/* Material Warnings (prohibitions and pricing warnings) */}
      <MaterialWarnings
        prohibitedMaterials={prohibitedMaterials}
        warnings={pricing?.warnings}
      />

      {/* Categorized Acknowledgments */}
      {pricing?.requiredAcknowledgments && pricing.requiredAcknowledgments.length > 0 && (
        <CategorizedAcknowledgments
          acknowledgments={pricing.requiredAcknowledgments}
          acknowledgedItems={acknowledgedTerms}
          onAcknowledgmentToggle={handleAcknowledgmentToggle}
        />
      )}

      {/* Placement Instructions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-50 rounded-lg">
            <InfoIcon className="h-5 w-5 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            Placement Instructions
          </h3>
        </div>

        <div>
          <label
            htmlFor="placementInstructions"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Special Instructions <span className="text-gray-500 font-normal">(Optional)</span>
          </label>
          <textarea
            {...register('placementInstructions')}
            id="placementInstructions"
            rows={5}
            maxLength={maxChars}
            placeholder="Example: Place container in driveway on the right side. Gate code is 1234."
            className="w-full min-h-[120px] px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400 resize-none"
            aria-label="Placement instructions for container delivery"
            aria-describedby="placement-help placement-count"
          />
          <div className="flex justify-between items-center mt-2">
            <p id="placement-help" className="text-sm text-gray-500">
              Help us deliver your container exactly where you need it
            </p>
            <span
              id="placement-count"
              className={`text-xs font-medium ${charCount > maxChars * 0.9 ? 'text-red-600' : 'text-gray-500'}`}
              aria-live="polite"
            >
              {charCount}/{maxChars}
            </span>
          </div>
        </div>

        {/* Helpful tips */}
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mt-4">
          <div className="flex">
            <svg
              className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-green-800">Helpful Tips</h4>
              <div className="mt-2 text-sm text-green-700">
                <ul className="list-disc pl-5 space-y-1">
                  <li>Ensure there is clear access for our delivery truck</li>
                  <li>Remove any obstacles from the placement area</li>
                  <li>Check for overhead power lines or tree branches</li>
                  <li>Confirm the surface can support the container weight</li>
                  <li>Provide gate codes or access instructions if needed</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Summary */}
      {providerSlug && (
        <RealTimePricing
          providerSlug={providerSlug}
          params={pricingParams}
        />
      )}

      {/* Acknowledgment validation warning */}
      {!allAcknowledgmentsMet && pricing?.requiredAcknowledgments && pricing.requiredAcknowledgments.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4" role="alert">
          <div className="flex">
            <svg
              className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">
                Please acknowledge all required items above before proceeding to checkout.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Order review notice */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <p className="text-sm text-gray-700">
          <span className="font-medium">Ready to proceed?</span> Click &quot;Proceed to
          Checkout&quot; to review your order and complete payment.
        </p>
      </div>
    </div>
  );
}
